import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Center, Spinner, Text, VStack } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { useUnit } from 'effector-react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { Button } from '@m/components-ui/button';
import { PAYMENT_OVERDUE_TODO } from '@m/constants/TodoTypes';
import { useCustomerFeatureFlag } from '@m/hooks/data/featureFlags';
import { useAutoAgreeToPendingAgreements } from '@m/hooks/data/terms';
import { useRequestCode, useUpdateUserInfo } from '@m/hooks/data/user';
import { addVehicle } from '@m/state/vehicle';
import { AddedVia, ProfileFieldSource } from '@m/types/grpc/MemberOnboardingSource';
import { useRequestVerifyCode } from 'apps/customer/containers/OnboardingFlow/hooks/useRequestVerifyCode';
import { useVerifyPhone } from 'apps/customer/containers/OnboardingFlow/hooks/useVerifyPhone';
import { useEnterpriseInvitationRedeem } from 'apps/customer/hooks/useEnterpriseInvitationRedeem';
import { useStripeClientSecret } from 'apps/customer/hooks/useStripeClientSecret';
import { useTrackViewedOnce } from 'apps/customer/hooks/useTrackViewedOnce';
import {
  DefaultOnboardingRoutes,
  ProtectedRoutes,
  signInOnboardingPaths,
} from 'apps/customer/routes/paths';
import {
  membershipStore,
  setIndividualSubscriptionInvitationId,
} from 'apps/customer/state/membership';
import { fetchTodoItems } from 'apps/customer/state/todo';
import { clearEnterpriseInvitationUser, getUser, userStore } from 'apps/customer/state/user';

import { deriveVariant } from '../deriveVariant';
import {
  trackOnboardingStepCompleted,
  trackOnboardingStepSkipped,
  trackOnboardingStepViewed,
} from '../onboardingHeap';
import {
  SUBSCRIPTION_NOTICE_PARAM,
  SUBSCRIPTION_NOTICE_UNAVAILABLE,
  SUBSCRIPTION_UNAVAILABLE_MESSAGE,
  SubscriptionUnavailableError,
} from '../subscriptionEligibility';

import ContainerHeader from './ContainerHeader';
import { onboardingSessionStore, setIsFlowComplete, setIsReturningUser } from './onboardingSession';
import { OnboardingStep } from './OnboardingStep';
import { resolveStepGuard } from './stepGuard';
import { isContactCollectedLast } from './stepOrder';
import { PATH_FOR_STEP, stepForPath } from './stepRoutes';
import { useIndividualSubscriptionEligibility } from './useIndividualSubscriptionEligibility';
import { useOnboardingFlowResolution } from './useOnboardingFlowResolution';
import { useReviewSubscriptionData } from './useReviewSubscriptionData';
import { useSignInCompletionPath } from './useSignInCompletionPath';
import { useSignInOnboardingNavigation } from './useSignInOnboardingNavigation';

import type { FlowNextAction } from './flowResolution';
import type { TodoItem } from '@m/types/api';

const loadContactInfoView = () => import('../views/ContactInfoView');
const loadPaymentView = () => import('../views/PaymentView');
const loadPhoneView = () => import('../views/PhoneView');
const loadReviewSubscriptionView = () => import('../views/ReviewSubscriptionView');
const loadSuccessView = () => import('../views/SuccessView');
const loadVehicleView = () => import('../views/VehicleView');
const loadVerifyCodeView = () => import('../views/VerifyCodeView');

const ContactInfoView = lazy(loadContactInfoView);
const PaymentView = lazy(loadPaymentView);
const PhoneView = lazy(loadPhoneView);
const ReviewSubscriptionView = lazy(loadReviewSubscriptionView);
const SuccessView = lazy(loadSuccessView);
const VehicleView = lazy(loadVehicleView);
const VerifyCodeView = lazy(loadVerifyCodeView);

function preloadStepViews() {
  void Promise.allSettled([
    loadContactInfoView(),
    loadPaymentView(),
    loadPhoneView(),
    loadReviewSubscriptionView(),
    loadSuccessView(),
    loadVehicleView(),
    loadVerifyCodeView(),
  ]);
}

function FlowLoading() {
  return (
    <Center height="100%">
      <Spinner />
    </Center>
  );
}

function FlowError({ message, retry }: { message: string; retry?: () => void }) {
  return (
    <Center height="100%" px="4">
      <VStack gap="4">
        <Text textAlign="center">{message}</Text>
        {retry && (
          <Button variant="secondary" onClick={retry}>
            Try again
          </Button>
        )}
      </VStack>
    </Center>
  );
}

export default function SignInOnboardingContainer() {
  return (
    <Route exact path={signInOnboardingPaths}>
      <SignInOnboardingBody />
    </Route>
  );
}

export function SignInOnboardingBody() {
  const location = useLocation();
  const rules = useOnboardingFlowResolution();
  const { finish, navigate } = useSignInOnboardingNavigation();
  const { user, phoneNumber, enterpriseUser } = useUnit(userStore);
  const currentStep = stepForPath(location.pathname);
  const [backOverride, setBackOverride] = useState<{ fn: () => void } | null>(null);
  const [isResolvingStep, setIsResolvingStep] = useState(false);
  // Set while the user swaps their card from the review page, so the guard doesn't bounce them
  // straight back to review (they already have a default payment method).
  const [isChangingPayment, setIsChangingPayment] = useState(false);

  useEffect(preloadStepViews, []);

  // Existing members keep the app: drop the subscription invite and send them to the dashboard,
  // which explains why via the notice param.
  const leaveForDashboard = () => {
    setIndividualSubscriptionInvitationId(null);
    finish(
      `${ProtectedRoutes.DashboardPage}?${SUBSCRIPTION_NOTICE_PARAM}=${SUBSCRIPTION_NOTICE_UNAVAILABLE}`,
      { replace: true },
    );
  };

  const guardAction = useMemo(
    () =>
      currentStep &&
      !isResolvingStep &&
      !(isChangingPayment && currentStep === OnboardingStep.AddPayment)
        ? resolveStepGuard(currentStep, {
            hasCompletedFlow: rules.hasCompletedFlow,
            hasVerificationPhone: rules.hasVerificationPhone,
            hasVerifiedSession: rules.hasVerifiedSession,
            resumeAction: rules.resumeAction,
            variant: rules.variant,
          })
        : null,
    [
      currentStep,
      isChangingPayment,
      isResolvingStep,
      rules.hasCompletedFlow,
      rules.hasVerifiedSession,
      rules.hasVerificationPhone,
      rules.resumeAction,
      rules.variant,
    ],
  );

  useEffect(() => {
    if (guardAction) navigate(guardAction);
  }, [guardAction, navigate]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      height="100dvh"
      bg="fill.background.default"
    >
      {currentStep && currentStep !== OnboardingStep.Success && (
        <Box flexShrink="0">
          <ContainerHeader
            currentStep={currentStep}
            variant={rules.variant}
            backOverride={backOverride}
            onBackToContact={() => navigate({ type: 'step', step: OnboardingStep.AddEmail })}
            onClose={() => {
              if (rules.variant === 'individualSub') {
                setIndividualSubscriptionInvitationId(null);
              } else if (rules.variant === 'enterpriseSub') {
                clearEnterpriseInvitationUser();
              }
              finish(ProtectedRoutes.HomePage, { preserveSearch: false, replace: true });
            }}
          />
        </Box>
      )}
      <Box flex="1" minH="0" overflow="hidden">
        <Suspense fallback={<FlowLoading />}>
          {/* While a redirect is pending, swap only the body: replacing the whole screen (header
              included) with a centered spinner made the layout jump between steps. The step itself
              stays unmounted so it never runs for a screen the user is about to leave. */}
          {guardAction ? (
            <FlowLoading />
          ) : (
            <Switch>
              <Route exact path={DefaultOnboardingRoutes.SignUp}>
                <Redirect to={PATH_FOR_STEP[OnboardingStep.AddEmail] + location.search} />
              </Route>
              <Route exact path={PATH_FOR_STEP[OnboardingStep.Landing]}>
                <LandingStep
                  phoneNumber={phoneNumber}
                  variant={rules.variant}
                  onContinue={navigate}
                />
              </Route>
              <Route exact path={PATH_FOR_STEP[OnboardingStep.VerifyPhone]}>
                <VerifyPhoneStep
                  phoneNumber={phoneNumber}
                  rules={rules}
                  onContinue={navigate}
                  onExistingUserUnavailable={leaveForDashboard}
                />
              </Route>
              <Route exact path={PATH_FOR_STEP[OnboardingStep.AddVehicle]}>
                <AddVehicleStep
                  rules={rules}
                  onContinue={navigate}
                  setBackOverride={setBackOverride}
                  setIsResolvingStep={setIsResolvingStep}
                />
              </Route>
              <Route exact path={PATH_FOR_STEP[OnboardingStep.AddPayment]}>
                <AddPaymentStep
                  userId={user?.id}
                  isChangingPayment={isChangingPayment}
                  onFinishChangingPayment={() => setIsChangingPayment(false)}
                  onBackToReview={() => {
                    setIsChangingPayment(false);
                    navigate({ type: 'step', step: OnboardingStep.ReviewSubscription });
                  }}
                  setBackOverride={setBackOverride}
                  rules={rules}
                  onContinue={navigate}
                  setIsResolvingStep={setIsResolvingStep}
                />
              </Route>
              <Route exact path={PATH_FOR_STEP[OnboardingStep.AddEmail]}>
                <AddEmailStep
                  phoneNumber={phoneNumber}
                  user={user}
                  enterpriseUser={enterpriseUser}
                  rules={rules}
                  onContinue={navigate}
                  onExistingUserUnavailable={leaveForDashboard}
                  setIsResolvingStep={setIsResolvingStep}
                />
              </Route>
              <Route exact path={PATH_FOR_STEP[OnboardingStep.ReviewSubscription]}>
                <ReviewSubscriptionStep
                  variant={rules.variant}
                  onContinue={navigate}
                  onExistingUserUnavailable={leaveForDashboard}
                  onChangePayment={() => {
                    setIsChangingPayment(true);
                    navigate({ type: 'step', step: OnboardingStep.AddPayment });
                  }}
                />
              </Route>
              <Route exact path={PATH_FOR_STEP[OnboardingStep.Success]}>
                <SuccessStep variant={rules.variant} />
              </Route>
            </Switch>
          )}
        </Suspense>
      </Box>
    </Box>
  );
}

function LandingStep({
  onContinue,
  phoneNumber,
  variant,
}: {
  onContinue: (action: FlowNextAction) => void;
  phoneNumber: string | null;
  variant: ReturnType<typeof deriveVariant>;
}) {
  const { mutateAsync: requestVerifyCode } = useRequestVerifyCode();
  const { data: hcaptchaEnabled = false } = useCustomerFeatureFlag('HcaptchaEnabled');
  useTrackViewedOnce(true, () => trackOnboardingStepViewed('Phone', variant));

  return (
    <PhoneView
      variant={variant}
      defaultValue={phoneNumber ?? ''}
      hcaptchaEnabled={hcaptchaEnabled}
      onContinue={async ({ hcaptchaToken, phoneNumber: entered }) => {
        setIsFlowComplete(false);
        await requestVerifyCode({ hcaptchaToken, phoneNumber: entered });
        trackOnboardingStepCompleted('Phone', variant);
        onContinue({ type: 'step', step: OnboardingStep.VerifyPhone });
      }}
    />
  );
}

function VerifyPhoneStep({
  onContinue,
  onExistingUserUnavailable,
  phoneNumber,
  rules,
}: {
  onContinue: (action: FlowNextAction) => void;
  onExistingUserUnavailable: () => void;
  phoneNumber: string | null;
  rules: ReturnType<typeof useOnboardingFlowResolution>;
}) {
  const { mutateAsync: verifyPhone } = useVerifyPhone();
  const { mutateAsync: resendCode } = useRequestCode();
  const autoAgreeToPendingAgreements = useAutoAgreeToPendingAgreements();
  const checkEligibility = useIndividualSubscriptionEligibility();
  useTrackViewedOnce(true, () => trackOnboardingStepViewed('VerifyCode', rules.variant));

  return (
    <VerifyCodeView
      phoneNumber={phoneNumber ?? undefined}
      onResend={
        phoneNumber
          ? async () => {
              await resendCode({ phoneNumber });
            }
          : undefined
      }
      onContinue={async ({ code }) => {
        const result = await verifyPhone({ otp: code });
        setIsReturningUser(!result.isNewAccount);
        setIsFlowComplete(false);
        try {
          await autoAgreeToPendingAgreements();
        } catch (error) {
          Sentry.captureException(error);
        }
        trackOnboardingStepCompleted('VerifyCode', rules.variant);
        const action = await rules.resolveAfterVerifyPhone(result);
        // The allow-list can match on phone or email. If the next step collects the email, defer
        // to it — blocking now would reject someone who's listed by email only.
        const emailStillToCollect =
          action.type === 'step' && action.step === OnboardingStep.AddEmail;
        if (rules.variant === 'individualSub' && !emailStillToCollect) {
          if (!(await checkEligibility())) {
            if (!result.isNewAccount) {
              onExistingUserUnavailable();
              return;
            }
            throw new SubscriptionUnavailableError();
          }
        }
        onContinue(action);
      }}
    />
  );
}

function AddVehicleStep({
  onContinue,
  rules,
  setBackOverride,
  setIsResolvingStep,
}: {
  onContinue: (action: FlowNextAction) => void;
  rules: ReturnType<typeof useOnboardingFlowResolution>;
  setBackOverride: (override: { fn: () => void } | null) => void;
  setIsResolvingStep: (isResolving: boolean) => void;
}) {
  const [todoLoadError, setTodoLoadError] = useState<string | null>(null);
  useTrackViewedOnce(true, () => trackOnboardingStepViewed('Vehicle', rules.variant));

  useEffect(
    () => () => {
      setIsResolvingStep(false);
    },
    [setIsResolvingStep],
  );

  const continueWithTodos = (todoItems: TodoItem[]) => {
    const hasPaymentTask = todoItems.some((todoItem) => todoItem.type === PAYMENT_OVERDUE_TODO);
    onContinue(rules.resolveAfterAddVehicle(hasPaymentTask));
  };

  const retryTodoLoad = async () => {
    try {
      const response = await fetchTodoItems();
      const todoItems = response.data?.todoItems;
      if (!response.success || !todoItems) {
        setTodoLoadError(response.error || 'Unable to load your next onboarding step.');
        return;
      }

      continueWithTodos(todoItems);
    } catch (error) {
      setTodoLoadError(
        error instanceof Error ? error.message : 'Unable to load your next onboarding step.',
      );
    }
  };

  if (todoLoadError) {
    return (
      <FlowError
        message={`Your vehicle was saved, but we couldn't load your next step. ${todoLoadError}`}
        retry={() => {
          void retryTodoLoad();
        }}
      />
    );
  }

  return (
    <VehicleView
      setBackOverride={setBackOverride}
      onContinue={async (values) => {
        setIsResolvingStep(true);
        const response = await addVehicle({
          ...values,
          addedVia: AddedVia.EXPRESS_ONBOARDING,
        }).catch((error: unknown) => {
          setIsResolvingStep(false);
          throw error;
        });
        if (!response.success) {
          setIsResolvingStep(false);
          throw new Error(response.error || 'Add Vehicle Failed');
        }

        trackOnboardingStepCompleted('Vehicle', rules.variant);
        const todoItems = response.data?.todoItems;
        if (!todoItems) {
          setTodoLoadError('Please try loading the next step again.');
          return;
        }

        continueWithTodos(todoItems);
      }}
    />
  );
}

function AddPaymentStep({
  isChangingPayment,
  onBackToReview,
  onContinue,
  onFinishChangingPayment,
  rules,
  setBackOverride,
  setIsResolvingStep,
  userId,
}: {
  isChangingPayment: boolean;
  onBackToReview: () => void;
  onContinue: (action: FlowNextAction) => void;
  onFinishChangingPayment: () => void;
  rules: ReturnType<typeof useOnboardingFlowResolution>;
  setBackOverride: (override: { fn: () => void } | null) => void;
  setIsResolvingStep: (isResolving: boolean) => void;
  userId: number | undefined;
}) {
  const { data: clientSecret, isError, isFetching, refetch } = useStripeClientSecret(userId);
  useTrackViewedOnce(!!clientSecret, () => trackOnboardingStepViewed('Payment', rules.variant));

  useEffect(
    () => () => {
      setIsResolvingStep(false);
    },
    [setIsResolvingStep],
  );

  // Swapping a card from the review page can be abandoned: give the header a way back to it.
  // The latest callback lives in a ref so the override is registered once per change, not on every
  // render (the callback is a new function each time and the override is state in the parent).
  const onBackToReviewRef = useRef(onBackToReview);
  useEffect(() => {
    onBackToReviewRef.current = onBackToReview;
  });
  useEffect(() => {
    if (!isChangingPayment) return undefined;
    setBackOverride({ fn: () => onBackToReviewRef.current() });
    return () => setBackOverride(null);
  }, [isChangingPayment, setBackOverride]);

  if (userId == null) return <FlowError message="Your account could not be loaded." />;
  if (isError) {
    return (
      <FlowError
        message="Your payment form could not be loaded."
        retry={() => {
          void refetch();
        }}
      />
    );
  }
  if (!clientSecret) return <FlowLoading />;

  return (
    <PaymentView
      clientSecret={clientSecret}
      userId={userId}
      variant={rules.variant}
      isFetching={isFetching}
      onFailed={() => setIsResolvingStep(false)}
      onSubmitStart={() => setIsResolvingStep(true)}
      onSuccess={() => {
        trackOnboardingStepCompleted('Payment', rules.variant);
        onFinishChangingPayment();
        onContinue(rules.resolveAfterAddPayment());
      }}
    />
  );
}

type ContactPrefillSource = {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

function AddEmailStep({
  enterpriseUser,
  onContinue,
  onExistingUserUnavailable,
  phoneNumber,
  rules,
  setIsResolvingStep,
  user,
}: {
  enterpriseUser: ContactPrefillSource | undefined;
  onContinue: (action: FlowNextAction) => void;
  onExistingUserUnavailable: () => void;
  phoneNumber: string | null;
  rules: ReturnType<typeof useOnboardingFlowResolution>;
  setIsResolvingStep: (isResolving: boolean) => void;
  user: ContactPrefillSource | null | undefined;
}) {
  const { mutateAsync: updateUserInfo } = useUpdateUserInfo();
  const isOptional = rules.contactInfoRequirement === 'none';
  const { isReturningUser } = useUnit(onboardingSessionStore);
  const checkEligibility = useIndividualSubscriptionEligibility();
  const advance = async () => {
    if (rules.variant === 'individualSub' && !(await checkEligibility())) {
      if (isReturningUser) {
        onExistingUserUnavailable();
        return;
      }
      throw new SubscriptionUnavailableError();
    }
    onContinue(rules.resolveAfterAddEmail());
  };
  useTrackViewedOnce(true, () =>
    trackOnboardingStepViewed('ContactInfo', rules.variant, isOptional),
  );

  useEffect(
    () => () => {
      setIsResolvingStep(false);
    },
    [setIsResolvingStep],
  );

  return (
    <ContactInfoView
      variant={rules.variant}
      requirement={rules.contactInfoRequirement}
      collectsName
      defaultValues={{
        firstName: user?.firstName ?? enterpriseUser?.firstName ?? '',
        lastName: user?.lastName ?? enterpriseUser?.lastName ?? '',
        email: user?.email ?? enterpriseUser?.email ?? '',
      }}
      onContinue={async ({ firstName, lastName, email }) => {
        const hasAnyInput = !!email || !!firstName || !!lastName;
        if (isOptional && !hasAnyInput) {
          trackOnboardingStepSkipped('ContactInfo', rules.variant, isOptional);
          await advance();
          return;
        }
        setIsResolvingStep(true);
        try {
          await updateUserInfo({
            firstName: firstName || undefined,
            lastName: lastName || undefined,
            email: email || undefined,
            phoneNumber: phoneNumber ?? undefined,
            profileFieldSource: ProfileFieldSource.EXPRESS_ONBOARDING,
          });
          await getUser();
          trackOnboardingStepCompleted('ContactInfo', rules.variant, isOptional);
          await advance();
        } catch (error) {
          setIsResolvingStep(false);
          throw error;
        }
      }}
    />
  );
}

/**
 * The allow-list is checked after verify and after the contact step, but a refresh or a direct
 * visit resumes the flow past both — so the review step checks again before it loads pricing or
 * prepares a cart. Nobody who isn't eligible gets to the purchase button.
 */
function ReviewSubscriptionStep({
  onChangePayment,
  onContinue,
  onExistingUserUnavailable,
  variant,
}: {
  onChangePayment: () => void;
  onContinue: (action: FlowNextAction) => void;
  onExistingUserUnavailable: () => void;
  variant: ReturnType<typeof deriveVariant>;
}) {
  const checkEligibility = useIndividualSubscriptionEligibility();
  const { isReturningUser } = useUnit(onboardingSessionStore);
  const [eligibility, setEligibility] = useState<'blocked' | 'checking' | 'eligible'>('checking');
  const onExistingUserUnavailableRef = useRef(onExistingUserUnavailable);

  useEffect(() => {
    onExistingUserUnavailableRef.current = onExistingUserUnavailable;
  });

  useEffect(() => {
    let isCurrent = true;
    void checkEligibility().then((isEligible) => {
      if (!isCurrent) return;
      if (isEligible) setEligibility('eligible');
      else if (isReturningUser) onExistingUserUnavailableRef.current();
      else setEligibility('blocked');
    });
    return () => {
      isCurrent = false;
    };
  }, [checkEligibility, isReturningUser]);

  if (eligibility === 'checking') return <FlowLoading />;
  if (eligibility === 'blocked') return <FlowError message={SUBSCRIPTION_UNAVAILABLE_MESSAGE} />;

  return (
    <EligibleReviewSubscriptionStep
      variant={variant}
      onChangePayment={onChangePayment}
      onContinue={onContinue}
    />
  );
}

function EligibleReviewSubscriptionStep({
  onChangePayment,
  onContinue,
  variant,
}: {
  onChangePayment: () => void;
  onContinue: (action: FlowNextAction) => void;
  variant: ReturnType<typeof deriveVariant>;
}) {
  const { individualSubscriptionInvitationId } = useUnit(membershipStore);
  const data = useReviewSubscriptionData(individualSubscriptionInvitationId);
  useTrackViewedOnce(data.status === 'ready', () =>
    trackOnboardingStepViewed('ReviewSubscription', variant),
  );

  if (data.status === 'loading') return <FlowLoading />;
  if (data.status === 'error') return <FlowError message={data.message} retry={data.retry} />;

  const { onContinue: purchase, ...viewProps } = data;
  return (
    <ReviewSubscriptionView
      {...viewProps}
      onChangePayment={onChangePayment}
      onContinue={async () => {
        await purchase();
        trackOnboardingStepCompleted('ReviewSubscription', variant);
        onContinue({ type: 'done' });
      }}
    />
  );
}

function SuccessStep({ variant }: { variant: ReturnType<typeof deriveVariant> }) {
  const { isReturningUser } = useUnit(onboardingSessionStore);
  const completionPath = useSignInCompletionPath();
  const { finish } = useSignInOnboardingNavigation();
  const isSubscription = variant === 'enterpriseSub' || variant === 'individualSub';
  const isNewUser = isReturningUser === false;
  const showsSuccessStep =
    isSubscription || isContactCollectedLast({ isNewAccount: isNewUser, variant });
  const { inviteRedemptionError, isRedeemingInvite, retryInviteRedemption } =
    useEnterpriseInvitationRedeem();
  const isSuccessVisible =
    showsSuccessStep &&
    !(variant === 'enterpriseSub' && (!!isRedeemingInvite || !!inviteRedemptionError));
  useTrackViewedOnce(isSuccessVisible, () => trackOnboardingStepViewed('Success', variant));

  useEffect(() => {
    if (!showsSuccessStep) finish(completionPath, { replace: true });
  }, [completionPath, finish, showsSuccessStep]);

  if (!showsSuccessStep) return <FlowLoading />;
  if (variant === 'enterpriseSub' && isRedeemingInvite) return <FlowLoading />;
  if (variant === 'enterpriseSub' && inviteRedemptionError) {
    return <FlowError message={inviteRedemptionError} retry={retryInviteRedemption} />;
  }

  return (
    <SuccessView
      isSubscription={isSubscription}
      doneLabel={variant === 'agnostic' ? 'Finish' : undefined}
      isNewUser={isNewUser}
      onDone={() => {
        if (variant === 'individualSub') setIndividualSubscriptionInvitationId(null);
        if (variant === 'enterpriseSub') clearEnterpriseInvitationUser();
        // Someone who just subscribed lands on their membership, whatever else (a deep link, an
        // active visit) would otherwise have decided the destination.
        finish(isSubscription ? ProtectedRoutes.MembershipPage : completionPath, {
          preserveSearch: !isSubscription,
          replace: isSubscription,
        });
      }}
    />
  );
}
